import {
  IConsequenceArray,
  IConsequenceWithId,
  IEvaluationCategoryApi,
  IIssueBoardApi,
  IMatrixConfig,
  IRisikoProbability,
  IUpdateConsequence,
} from "@mcapps/shared";
import { ICapitalLetter } from "@schemas/otherAttributesSchema";
import { CellStyleFunc, ColDef, ColDefField } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import React from "react";
import { FailedAlert, McTitle } from "../../../../../../components";
import ViewEditTable from "../../../../../../components/ViewEditTable/ViewEditTable";
import { STANDARD_COLORS } from "../../../../../globalConstants";
import { usePostProjectByProjectIdIssueboardRisikoEvaluationcategoryUpdateMutation } from "../../../../../../../openapi";

const ColorMatrixSettings: React.FC<{ issueBoard: IIssueBoardApi; evaluationCategoryKey: ICapitalLetter }> = ({
  issueBoard,
  evaluationCategoryKey,
}) => {
  const [updateEvaluationCategory, { error: updateEvaluationCategoryError }] =
    usePostProjectByProjectIdIssueboardRisikoEvaluationcategoryUpdateMutation();

  const consequenceOnXAxis = issueBoard.risikoSection?.consequenceOnXAxis === true;
  const pointsColumns = getRisikoPointsColumns(issueBoard, consequenceOnXAxis, evaluationCategoryKey);

  const pointsData: Record<string, any[]> = getRisikoPointsData(issueBoard, consequenceOnXAxis);

  const saveCategory = async (
    prop: ColDefField<IEvaluationCategoryApi>,
    newValue: string | boolean | IConsequenceArray | IMatrixConfig,
    id: string,
  ) => {
    const send = {
      projectId: issueBoard.project,
      body: {
        issueBoardId: issueBoard._id,
        prop,
        newValue: newValue as any,
        id,
      },
    };
    await updateEvaluationCategory(send);
  };

  const saveColors = async (
    id: string,
    data: {
      id: string;
      [key: string]: { color: string } | string;
    }[],
  ) => {
    const matrixConfig: IMatrixConfig = data.map((item) =>
      Object.entries(item).reduce((acc, [key, value]) => {
        if (key === "id" || key === "isActive") {
          return acc;
        }

        acc.push(value as { color: string });
        return acc;
      }, [] as { color: string }[]),
    );

    if (consequenceOnXAxis) {
      const category = issueBoard.risikoSection?.evaluationCategoriesConfig[id];
      const consequences = Object.entries(category?.consequences ?? {}).filter(([, consequence]) => consequence.isActive);
      const probabilities = Object.entries(issueBoard.risikoSection?.probabilites ?? {}).filter(([, probability]) => probability.isActive);
      const originalMatrix = category?.matrixConfig.map((row) => [...row]) ?? [];

      consequences.forEach(([consequenceId, consequence]) => {
        const consequenceIndex = Object.keys(category?.consequences ?? {}).indexOf(consequenceId);
        probabilities.forEach(([probabilityId, probability]) => {
          const probabilityIndex = Object.keys(issueBoard.risikoSection?.probabilites ?? {}).indexOf(probabilityId);
          const row = data.find((item) => item.id === probability.name);
          const value = row?.[consequence.name] as { color?: string } | string | undefined;
          const color = typeof value === "string" ? value : value?.color;
          if (color != null) {
            originalMatrix[consequenceIndex] ??= [];
            originalMatrix[consequenceIndex][probabilityIndex] = { color };
          }
        });
      });

      saveCategory("matrixConfig", originalMatrix, id);
      return;
    }

    saveCategory("matrixConfig", matrixConfig, id);
  };

  return (
    <div className="risiko-color-tables">
      {updateEvaluationCategoryError && <FailedAlert />}
      <McTitle title="Fargeinnstillinger for matrise">
        <ViewEditTable
          columns={pointsColumns}
          data={pointsData[evaluationCategoryKey] ?? []}
          newItemTemplateObject={{}}
          onSave={(data) => saveColors(evaluationCategoryKey, data)}
          canEdit={true}
          canAddRows={false}
        />
      </McTitle>
    </div>
  );
};

export default ColorMatrixSettings;

const ColorMenuRenderer = ({ value, data }: CustomCellRendererProps) => {
  return typeof value === "string" && data?.isActive !== false ? (
    <span
      style={{
        display: "inline-block",
        minWidth: "120px",
        height: "20px",
        borderRadius: "5px",
        backgroundColor: value,
        marginRight: "5px",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      <span>{value === "ffffff00" && "Velg farge"}</span>
    </span>
  ) : (
    <></>
  );
};

const isActiveCellStyle: CellStyleFunc<IConsequenceWithId> = (params) => {
  if (params.data.isActive === false) {
    return { backgroundColor: "#f0f0f0" };
  }

  return {};
};

export const getRisikoPointsData = (issueBoard: IIssueBoardApi, consequenceOnXAxis = false): Record<string, any[]> => {
  try {
    const _data: Record<string, any[]> = Object.entries(issueBoard.risikoSection.evaluationCategoriesConfig).reduce(
      (acc, [boardId, evaluationCat]: [key: string, prob: IEvaluationCategoryApi]) => {
        if (evaluationCat.isActive === false) {
          return acc;
        }

        if (acc[boardId] == null) {
          acc[boardId] = [];
        }

        if (consequenceOnXAxis) {
          Object.entries(issueBoard.risikoSection.probabilites)
            .filter(([, probability]) => probability.isActive)
            .reverse()
            .forEach(([probabilityKey, probability]) => {
              const probabilityIndex = Object.keys(issueBoard.risikoSection.probabilites).indexOf(probabilityKey);
              const row: Record<string, unknown> = { id: probability.name, isActive: probability.isActive };

              Object.entries(evaluationCat.consequences).forEach(([consequenceKey, consequence]) => {
                const consequenceIndex = Object.keys(evaluationCat.consequences).indexOf(consequenceKey);
                row[consequence.name] = evaluationCat.matrixConfig[consequenceIndex]?.[probabilityIndex] ?? { color: "#ccc" };
              });

              acc[boardId].push(row);
            });
          return acc;
        }

        Object.entries(evaluationCat.consequences).forEach(([key, value], i) => {
          if (acc[boardId][i] == null) {
            acc[boardId][i] = { id: value.name, isActive: value.isActive };
          }

          Object.entries(issueBoard.risikoSection.probabilites).forEach(
            ([k, probability]: [string, IRisikoProbability], rowIndex) => {
              const matricConfig = (
                issueBoard.risikoSection.evaluationCategoriesConfig[boardId] as IEvaluationCategoryApi
              )?.matrixConfig;

              acc[boardId][i][probability.name] = matricConfig[i]?.[rowIndex] ?? {
                color: "#ccc",
                isActive: value.isActive ?? true,
              };
            },
          );
        });

        return acc;
      },
      {},
    );
    return _data;
  } catch (err) {
    console.error(err);
    return {};
  }
};

export const getRisikoPointsColumns = (
  issueBoard: IIssueBoardApi,
  consequenceOnXAxis = false,
  evaluationCategoryKey?: ICapitalLetter,
): ColDef<Record<string, any>>[] => {
  if (issueBoard.risikoSection.probabilites == null) {
    return [];
  }

  const axisValues = consequenceOnXAxis
    ? Object.entries(issueBoard.risikoSection.evaluationCategoriesConfig[evaluationCategoryKey ?? ""]?.consequences ?? {})
    : Object.entries(issueBoard.risikoSection.probabilites);
  const _cols: ColDef[] = axisValues.reduce(
    (acc, [key, axisValue]: [string, IRisikoProbability]) => {
      if (axisValue.isActive === false) {
        return acc;
      }

      const _col: ColDef = {
        field: axisValue.name + ".color",
        headerName: axisValue.name,
        flex: 1,
        sortable: false,
        editable: (params) => params.data.isActive,
        cellRenderer: ColorMenuRenderer,
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          cellRenderer: ColorMenuRenderer,
          values: Object.values(STANDARD_COLORS),
        },
        cellStyle: isActiveCellStyle,
      };

      acc.push(_col);

      return acc;
    },
    [
      {
        field: "id",
        headerName: consequenceOnXAxis ? "Sannsynlighet" : "Konsekvens",
        flex: 1,
        cellStyle: isActiveCellStyle,
      } as ColDef,
    ],
  );

  return _cols;
};
